/**
 * Reconciles a product selection against a freshly filtered product list.
 *
 * Product rules can remove a product from the catalogue as the customer fills
 * in the vehicle — pick "DPF Off", then type gearbox "DSG", and it is no longer
 * orderable. Leaving it selected would show a phantom line and an inflated
 * credit total, and the backend would reject the order anyway, so the selection
 * has to follow the list.
 *
 * Kept pure and separate from the stepper so it can be tested directly.
 */

export interface SelectableProduct {
  id: number;
  name?: string;
  [key: string]: unknown;
}

export interface ReconcileResult {
  /** Ids still present in the new product list, in their original order. */
  kept: number[];
  /** Ids that disappeared, with whatever we knew about them. */
  dropped: Array<{ id: number; name?: string }>;
  /** True when anything was dropped — cheaper than comparing arrays. */
  changed: boolean;
}

export const reconcileSelection = (
  selectedIds: readonly number[],
  products: readonly SelectableProduct[],
  /** Names for ids no longer in `products`, so the warning can name them. */
  knownNames: ReadonlyMap<number, string> = new Map(),
): ReconcileResult => {
  const availableIds = new Set(products.map((product) => product.id));

  const kept: number[] = [];
  const dropped: Array<{ id: number; name?: string }> = [];

  for (const id of selectedIds) {
    if (availableIds.has(id)) {
      kept.push(id);
    } else {
      dropped.push({ id, name: knownNames.get(id) });
    }
  }

  return { kept, dropped, changed: dropped.length > 0 };
};

/**
 * Vehicle context sent to `GET /products/prices` so the API can apply product
 * rules. Note `calculator` is the order form's field name for the ECU brand;
 * the API calls it `ecu`.
 */
export interface VehicleContext {
  make?: string;
  model?: string;
  gearbox?: string;
  engine?: string;
  ecu?: string;
}

/** Drops blank values so a half-filled form sends no useless params. */
export const buildVehicleContext = (values: {
  make?: unknown;
  model?: unknown;
  gearbox?: unknown;
  engine?: unknown;
  calculator?: unknown;
}): VehicleContext => {
  const clean = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const context: VehicleContext = {
    make: clean(values.make),
    model: clean(values.model),
    gearbox: clean(values.gearbox),
    engine: clean(values.engine),
    ecu: clean(values.calculator),
  };

  // Strip undefined so the query string stays clean and the react-query key
  // is stable for equivalent contexts.
  return Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined),
  ) as VehicleContext;
};
